import { useCallback, useEffect, useState, type FormEvent } from "react";
import { C, font } from "../theme";
import { Card } from "./Card";
import { Label } from "./Label";
import { Button } from "./Button";
import { FieldInput, FieldTextarea } from "./Field";
import { money } from "../lib/format";
import { useSession } from "../lib/session/SessionContext";
import { canViewCashHistory } from "../lib/access";
import { ApiError } from "../lib/api/client";
import {
  closeCashSession,
  getCashSessionDetails,
  getCurrentCashSession,
  openCashSession,
  recordCashTransaction,
} from "../lib/api/endpoints";
import {
  CASH_SESSION_STATUS,
  CASH_TRANSACTION_TYPE,
  cashTransactionTypeLabel,
  paymentMethodLabel,
} from "../lib/enums";
import type { CashSessionDetailsDto, CashSessionDto } from "../types";

type MovementType = typeof CASH_TRANSACTION_TYPE.Supply | typeof CASH_TRANSACTION_TYPE.Bleed;

const statusBadge: Record<number, { text: string; bg: string; color: string }> = {
  [CASH_SESSION_STATUS.Open]: { text: "Caixa aberto", bg: C.limeSoft, color: C.ink },
  [CASH_SESSION_STATUS.Closed]: { text: "Caixa fechado", bg: C.faint, color: C.gray },
};

function sumByPaymentMethod(details: CashSessionDetailsDto | null, method: number): number {
  if (!details) return 0;
  const value = details.salesByPaymentMethod[String(method)];
  return typeof value === "number" ? value : 0;
}

export function CashierTab() {
  const { accessToken, activeTenant } = useSession();
  const token = accessToken ?? "";
  const canViewHistory = canViewCashHistory(activeTenant?.roleSlug ?? null);

  const [session, setSession] = useState<CashSessionDto | null | undefined>(undefined);
  const [details, setDetails] = useState<CashSessionDetailsDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [openBalance, setOpenBalance] = useState("");
  const [openNotes, setOpenNotes] = useState("");
  const [openError, setOpenError] = useState<string | null>(null);

  const [movementType, setMovementType] = useState<MovementType>(CASH_TRANSACTION_TYPE.Supply);
  const [movementAmount, setMovementAmount] = useState("");
  const [movementDesc, setMovementDesc] = useState("");
  const [movementError, setMovementError] = useState<string | null>(null);
  const [movementDone, setMovementDone] = useState<string | null>(null);

  const [closing, setClosing] = useState(false);
  const [declaredBalance, setDeclaredBalance] = useState("");
  const [closeNotes, setCloseNotes] = useState("");
  const [closeError, setCloseError] = useState<string | null>(null);

  const [refreshKey, setRefreshKey] = useState(0);
  const reload = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setError(null);
    getCurrentCashSession(token)
      .then(s => {
        if (cancelled) return;
        setSession(s);
        if (s?.status === CASH_SESSION_STATUS.Closed) {
          setClosing(false);
        }
      })
      .catch(e => {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.detail ?? e.message : "Falha ao carregar o caixa");
      });
    return () => {
      cancelled = true;
    };
  }, [token, refreshKey]);

  const loadDetails = () => {
    if (!token || !session) return;
    setError(null);
    getCashSessionDetails(token, session.id)
      .then(setDetails)
      .catch(e => setError(e instanceof ApiError ? e.detail ?? e.message : "Falha ao carregar o extrato"));
  };

  const handleOpen = async (e: FormEvent) => {
    e.preventDefault();
    const balance = Number(openBalance);
    if (openBalance.trim() === "" || Number.isNaN(balance) || balance < 0) {
      setOpenError("Fundo inicial deve ser maior ou igual a zero");
      return;
    }
    if (openNotes.length > 255) {
      setOpenError("Observações devem ter no máximo 255 caracteres");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    setOpenError(null);
    try {
      await openCashSession(token, { openingBalance: balance, notes: openNotes.trim() || null });
      setOpenBalance("");
      setOpenNotes("");
      setSession(null);
      reload();
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.detail ?? err.message : "Falha ao abrir o caixa");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMovement = async (e: FormEvent) => {
    e.preventDefault();
    const amount = Number(movementAmount);
    if (movementAmount.trim() === "" || Number.isNaN(amount) || amount <= 0) {
      setMovementError("Valor deve ser maior que zero");
      return;
    }
    if (movementDesc.length > 255) {
      setMovementError("Motivo deve ter no máximo 255 caracteres");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    setMovementError(null);
    try {
      await recordCashTransaction(token, {
        type: movementType,
        amount,
        description: movementDesc.trim() || null,
      });
      setMovementAmount("");
      setMovementDesc("");
      setMovementDone(movementType === CASH_TRANSACTION_TYPE.Supply ? "Suprimento lançado" : "Sangria lançada");
      setDetails(null);
      reload();
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.detail ?? err.message : "Falha ao lançar movimento");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = async (e: FormEvent) => {
    e.preventDefault();
    const declared = Number(declaredBalance);
    if (declaredBalance.trim() === "" || Number.isNaN(declared) || declared < 0) {
      setCloseError("Informe o valor em caixa");
      return;
    }    if (closeNotes.length > 255) {
      setCloseError("Observações devem ter no máximo 255 caracteres");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    setCloseError(null);
    try {
      const closed = await closeCashSession(token, { declaredBalance: declared, notes: closeNotes.trim() || null });
      setSession(closed);
      setClosing(false);
      setDeclaredBalance("");
      setCloseNotes("");
      setDetails(null);
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.detail ?? err.message : "Falha ao fechar o caixa");
    } finally {
      setSubmitting(false);
    }
  };

  const showStatement = details !== null;
  const difference = session?.differenceAmount ?? null;

  return (
    <>
      <Card>
        {session === undefined ? (
          <div style={{ color: C.light, fontSize: 13, padding: "18px 4px" }}>Carregando caixa...</div>
        ) : session === null ? (
          <form onSubmit={handleOpen} noValidate>
            <Label>Caixa — nenhuma sessão aberta</Label>
            <p style={{ fontFamily: font, fontSize: 13, color: C.gray, margin: "0 0 14px", lineHeight: 1.5 }}>
              Para vender no balcão, abra o caixa com o fundo de troco disponível.
            </p>
            <label htmlFor="opening-balance" style={{ display: "block", fontFamily: font, fontSize: 12, fontWeight: 600, color: C.gray, marginBottom: 4 }}>
              Fundo inicial (R$) *
            </label>
            <FieldInput
              id="opening-balance"
              type="number"
              min={0}
              step="0.01"
              value={openBalance}
              onChange={e => setOpenBalance(e.target.value)}
              placeholder="Ex: 100"
              style={{ maxWidth: 220 }}
            />
            <label htmlFor="open-notes" style={{ display: "block", fontFamily: font, fontSize: 12, fontWeight: 600, color: C.gray, marginTop: 12, marginBottom: 4 }}>
              Observações
            </label>
            <FieldInput id="open-notes" value={openNotes} onChange={e => setOpenNotes(e.target.value)} placeholder="Opcional" style={{ maxWidth: 320 }} />
            {openError && <div style={{ color: C.red, fontSize: 11.5, marginTop: 6 }}>{openError}</div>}
            {submitError && <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: "C.redSoft", color: C.red, fontSize: 12.5 }}>{submitError}</div>}
            <Button type="submit" disabled={submitting} style={{ marginTop: 16 }}>
              {submitting ? "Abrindo..." : "Abrir caixa"}
            </Button>
          </form>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Label style={{ margin: 0 }}>Caixa</Label>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    padding: "3px 10px",
                    borderRadius: 12,
                    background: statusBadge[session.status]?.bg ?? C.faint,
                    color: statusBadge[session.status]?.color ?? C.gray,
                  }}
                >
                  {statusBadge[session.status]?.text ?? `Status ${session.status}`}
                </span>
              </div>
              {session.status === CASH_SESSION_STATUS.Open && (
                <div style={{ display: "inline-flex", gap: 8 }}>
                  {canViewHistory && !showStatement && (
                    <Button variant="outline" onClick={loadDetails} disabled={submitting} style={{ fontSize: 12 }}>
                      Ver extrato
                    </Button>
                  )}
                  {!closing && (
                    <Button variant="outline" onClick={() => { setClosing(true); setCloseError(null); }} disabled={submitting} style={{ fontSize: 12 }}>
                      Fechar caixa
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12, marginTop: 14 }}>
              <div>
                <div style={{ fontSize: 11, color: C.light }}>Fundo inicial</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.ink }}>{money(session.openingBalance)}</div>
              </div>
              {session.status === CASH_SESSION_STATUS.Open && (
                <div>
                  <div style={{ fontSize: 11, color: C.light }}>Aberto em</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: C.ink }}>{new Date(session.openedAt).toLocaleString("pt-BR")}</div>
                </div>
              )}
              {session.status === CASH_SESSION_STATUS.Closed && difference !== null && (
                <>
                  <div>
                    <div style={{ fontSize: 11, color: C.light }}>Saldo calculado</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.ink }}>{money(session.calculatedBalance ?? 0)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: C.light }}>Quebra de caixa</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: difference >= 0 ? C.ink : C.red }}>
                      {money(difference)}
                      {difference >= 0 ? " (sobra)" : " (falta)"}
                    </div>
                  </div>
                </>
              )}
            </div>

            {session.status === CASH_SESSION_STATUS.Open && !closing && (
              <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${C.faint}` }}>
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  <Button
                    variant={movementType === CASH_TRANSACTION_TYPE.Supply ? "dark" : "outline"}
                    onClick={() => { setMovementType(CASH_TRANSACTION_TYPE.Supply); setMovementDone(null); setMovementError(null); }}
                    style={{ fontSize: 12 }}
                  >
                    Suprimento
                  </Button>
                  <Button
                    variant={movementType === CASH_TRANSACTION_TYPE.Bleed ? "dark" : "outline"}
                    onClick={() => { setMovementType(CASH_TRANSACTION_TYPE.Bleed); setMovementDone(null); setMovementError(null); }}
                    style={{ fontSize: 12 }}
                  >
                    Sangria
                  </Button>
                </div>
                <form onSubmit={handleMovement} noValidate style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                  <div>
                    <label htmlFor="movement-amount" style={{ display: "block", fontFamily: font, fontSize: 12, fontWeight: 600, color: C.gray, marginBottom: 4 }}>
                      Valor (R$) *
                    </label>
                    <FieldInput
                      id="movement-amount"
                      type="number"
                      min={0.01}
                      step="0.01"
                      value={movementAmount}
                      onChange={e => setMovementAmount(e.target.value)}
                      placeholder="Ex: 50"
                      style={{ maxWidth: 160 }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <label htmlFor="movement-desc" style={{ display: "block", fontFamily: font, fontSize: 12, fontWeight: 600, color: C.gray, marginBottom: 4 }}>
                      Motivo *
                    </label>
                    <FieldInput
                      id="movement-desc"
                      value={movementDesc}
                      onChange={e => setMovementDesc(e.target.value)}
                      placeholder={movementType === CASH_TRANSACTION_TYPE.Supply ? "Ex: reforçar troco" : "Ex: pagar fornecedor"}
                    />
                  </div>
                  <Button type="submit" disabled={submitting} style={{ fontSize: 12 }}>
                    {submitting ? "Lançando..." : "Lançar"}
                  </Button>
                </form>
                {movementError && <div style={{ color: C.red, fontSize: 11.5, marginTop: 6 }}>{movementError}</div>}
                {movementDone && <div style={{ color: C.ink, fontSize: 12, fontWeight: 600, marginTop: 8 }}>{movementDone}</div>}
              </div>
            )}

            {session.status === CASH_SESSION_STATUS.Open && closing && (
              <form onSubmit={handleClose} noValidate style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${C.faint}` }}>
                <Label style={{ color: C.ink }}>Fechamento cego</Label>
                <p style={{ fontFamily: font, fontSize: 12.5, color: C.gray, margin: "0 0 12px", lineHeight: 1.5 }}>
                  Informe o valor físico contado na gaveta. O saldo calculado só será exibido após a confirmação.
                </p>
                <label htmlFor="declared-balance" style={{ display: "block", fontFamily: font, fontSize: 12, fontWeight: 600, color: C.gray, marginBottom: 4 }}>
                  Valor em caixa (R$) *
                </label>
                <FieldInput
                  id="declared-balance"
                  type="number"
                  min={0}
                  step="0.01"
                  value={declaredBalance}
                  onChange={e => setDeclaredBalance(e.target.value)}
                  placeholder="Ex: 250"
                  style={{ maxWidth: 220 }}
                />
                <label htmlFor="close-notes" style={{ display: "block", fontFamily: font, fontSize: 12, fontWeight: 600, color: C.gray, marginTop: 12, marginBottom: 4 }}>
                  Observações
                </label>
                <FieldInput id="close-notes" value={closeNotes} onChange={e => setCloseNotes(e.target.value)} placeholder="Opcional" style={{ maxWidth: 320 }} />
                {closeError && <div style={{ color: C.red, fontSize: 11.5, marginTop: 6 }}>{closeError}</div>}
                {submitError && <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: "C.redSoft", color: C.red, fontSize: 12.5 }}>{submitError}</div>}
                <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                  <Button type="submit" disabled={submitting} style={{ fontSize: 12 }}>
                    {submitting ? "Fechando..." : "Confirmar fechamento"}
                  </Button>
                  <Button variant="outline" onClick={() => setClosing(false)} disabled={submitting} style={{ fontSize: 12 }}>
                    Cancelar
                  </Button>
                </div>
              </form>
            )}

            {error && <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: "C.redSoft", color: C.red, fontSize: 12.5 }}>{error}</div>}
            {submitError && session.status === CASH_SESSION_STATUS.Open && !closing && (
              <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: "C.redSoft", color: C.red, fontSize: 12.5 }}>{submitError}</div>
            )}
          </>
        )}
      </Card>

      {showStatement && details && (
        <Card style={{ marginTop: 12 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Label style={{ margin: 0 }}>Extrato do turno</Label>
            <Button variant="ghost" onClick={() => setDetails(null)} ariaLabel="Fechar extrato" style={{ fontSize: 14, lineHeight: 1 }}>
              ✕
            </Button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginTop: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: C.light }}>Suprimentos</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{money(details.suppliesTotal)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.light }}>Sangrias</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{money(details.bleedingsTotal)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.light }}>Vendas Dinheiro</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{money(sumByPaymentMethod(details, 1))}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.light }}>Vendas Pix</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{money(sumByPaymentMethod(details, 2))}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.light }}>Vendas Cartão</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>
                {money(sumByPaymentMethod(details, 3) + sumByPaymentMethod(details, 4))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.light }}>Saldo calculado</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>{money(details.calculatedBalance)}</div>
            </div>
          </div>

          {details.transactions.length === 0 ? (
            <div style={{ color: C.light, fontSize: 13, padding: "16px 4px" }}>Nenhuma transação no turno.</div>
          ) : (
            <div style={{ overflowX: "auto", marginTop: 12 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 520 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: C.light, fontSize: 11 }}>
                    <th style={{ padding: "6px 4px" }}>TIPO</th>
                    <th>MÉTODO</th>
                    <th>VALOR</th>
                    <th>MOTIVO</th>
                  </tr>
                </thead>
                <tbody>
                  {details.transactions.map(t => (
                    <tr key={t.id} style={{ borderTop: `1px solid ${C.faint}` }}>
                      <td style={{ padding: "9px 4px", fontWeight: 600, color: C.ink }}>{cashTransactionTypeLabel(t.type)}</td>
                      <td style={{ color: C.gray }}>{paymentMethodLabel(t.paymentMethod)}</td>
                      <td style={{ fontWeight: 700, color: C.ink }}>{money(t.amount)}</td>
                      <td style={{ color: C.gray }}>{t.description ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </>
  );
}
